/*
 * Circadia Playbook detail — Phase 4B Commit 4Bb stage 2B.
 *
 * Powers livecircadia.ca/pages/playbook-<slug>.
 *
 * RESPONSIBILITIES
 *   1. Fetch /apps/dashboard/playbook/<slug> (App Proxy → Railway).
 *      Same-origin from the browser's POV; Shopify's storefront-digest
 *      cookie carries the password-wall session pre-launch.
 *   2. Hydrate the masthead (title / tagline / persona description /
 *      issue meta / cover label).
 *   3. Render the 4 modules' day cards for the CURRENT GLOBAL POSITION
 *      (default = 2 per D14). The position is a 1-4 index; each module
 *      maps it to its own toggle-level key via the toggleLevels array
 *      from the API payload.
 *   4. Wire the SYNCED toggle (D10): clicking ANY pill in ANY module's
 *      row sets the SAME global position for all 4 modules, then re-
 *      renders each module's visible day grid from the cached payload.
 *      No refetch — the full 4-level × 7-day grid lives in memory.
 *   5. Wire the module-tab switcher. Tab switching PRESERVES the
 *      current position (does not reset to default).
 *   6. Render the grocery list for the current position. Cart button
 *      visible only when circadia_products[] is non-empty.
 *
 * PLACEHOLDER RULES (D12)
 *   - Fuel: hide a meal's macro row when all 4 macros (kcal, protein,
 *     carbs, fat) === 0. Keep the description.
 *   - Heal: hide a sub-card whose title starts with "TODO — ". Other
 *     sub-cards render normally.
 *   - day_tag: render VERBATIM, including any "[COPY] " prefix. The
 *     [COPY] prefix is editorial metadata signalling Eric needs to
 *     differentiate that level later; it's a visible signal to the
 *     viewer that this level reuses the medium content.
 *
 * STYLE
 *   - Vanilla JS, IIFE wrapper, no framework, no build step. Mirrors
 *     customer-dashboard.js + admin-playbook-list.js conventions.
 */
(function () {
  'use strict';

  // ─── Constants ──────────────────────────────────────────────────────

  var MODULE_KEYS = ['fuel', 'move', 'heal', 'sync'];
  var MODULE_EYEBROWS = {
    fuel: 'FUEL · What goes in the body',
    move: 'MOVE · How the body trains',
    heal: 'HEAL · How the body recovers',
    sync: 'SYNC · How the mind settles',
  };
  var TOGGLE_LABEL_BY_MODULE = {
    fuel: 'Portion Size',
    move: 'Intensity',
    heal: 'Protocol Depth',
    sync: 'Depth',
  };
  // Each module's 4 toggle keys in POSITION ORDER (1-indexed). D10 binds
  // these positions across all 4 modules.
  var LEVELS_BY_MODULE = {
    fuel: ['small', 'medium', 'large', 'xl'],
    move: ['easing_in', 'steady', 'strong', 'peak'],
    heal: ['light', 'routine', 'diligent', 'restorative'],
    sync: ['light', 'steady', 'deep', 'profound'],
  };
  var LEVEL_DISPLAY_BY_MODULE = {
    fuel: { small: 'Small', medium: 'Medium', large: 'Large', xl: 'XL' },
    move: { easing_in: 'Easing In', steady: 'Steady', strong: 'Strong', peak: 'Peak' },
    heal: { light: 'Light', routine: 'Routine', diligent: 'Diligent', restorative: 'Restorative' },
    sync: { light: 'Light', steady: 'Steady', deep: 'Deep', profound: 'Profound' },
  };
  var DEFAULT_POSITION = 2;  // D14
  var DAY_LABELS = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };
  var DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

  // Inline SVG icons — copied verbatim from design/project/page-playbooks.jsx
  // (meal-slot icons :504-507, Heal sub-card icons :545-547). 14×14 each,
  // stroke=currentColor so the .pb-fuel-label color rule controls them.
  var ICON_MORNING = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="5"/><line x1="12" y1="19" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.05" y2="7.05"/><line x1="16.95" y1="16.95" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="5" y2="12"/><line x1="19" y1="12" x2="22" y2="12"/></svg>';
  var ICON_MIDDAY  = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22"/><line x1="2" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22" y2="12"/></svg>';
  var ICON_SNACKS  = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2 L14 8 L20 8 L15 12 L17 18 L12 14 L7 18 L9 12 L4 8 L10 8 Z"/></svg>';
  var ICON_EVENING = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M16 5 a 9 9 0 1 0 5 16 a 7 7 0 1 1 -5 -16 Z"/></svg>';
  var ICON_STRETCH = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M4 16 Q12 4 20 16"/></svg>';
  var ICON_SLEEP   = ICON_EVENING;  // design uses the moon glyph for both
  var ICON_RECOVER = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M8 12 L11 15 L16 9"/></svg>';

  var FUEL_SLOT_ICONS = { morning: ICON_MORNING, midday: ICON_MIDDAY, snacks: ICON_SNACKS, evening: ICON_EVENING };
  var HEAL_SUB_ICONS  = { stretch: ICON_STRETCH, sleep: ICON_SLEEP, recover: ICON_RECOVER };

  // ─── Helpers ────────────────────────────────────────────────────────

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.from((root || document).querySelectorAll(sel)); }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function clearChildren(el) {
    while (el && el.firstChild) el.removeChild(el.firstChild);
  }

  // ─── Module-scoped state (one playbook per page) ────────────────────

  var root = $('[data-pb-root]');
  var modulesRoot = $('[data-pb-modules]');
  if (!root || !modulesRoot) return;
  var slug = root.getAttribute('data-pb-slug');
  if (!slug) return;

  /** The full API payload — { id, published_at, slug, ...bulk_paste }.
   *  Cached here so the synced toggle re-renders without refetching. */
  var payload = null;

  /** Global position 1-4 (D10 / D14). The single source of truth for
   *  what level every module is currently showing. */
  var currentPosition = DEFAULT_POSITION;

  /** Shared open-state for day cards across ALL modules. Survives:
   *    (a) portion-toggle re-renders (renderAllModuleDays wipes innerHTML)
   *    (b) tab switches (bindTabs re-applies on panel show)
   *  Members are day_of_week strings ('mon'..'sun'). One Set, one source
   *  of truth — expanding Mon anywhere expands Mon everywhere. */
  var openDays = new Set();

  /** Variant-id map { handle: id } from the inline Liquid-rendered JSON. */
  var variantMap = (function readVariantMap() {
    var el = $('[data-pb-variant-map]');
    if (!el) return {};
    try { return JSON.parse(el.textContent); } catch (e) { return {}; }
  })();

  // ─── Fetch ──────────────────────────────────────────────────────────

  function fetchPlaybook() {
    var url = '/apps/dashboard/playbook/' + encodeURIComponent(slug);
    return fetch(url, { credentials: 'same-origin' }).then(function (res) {
      if (res.status === 404) throw new Error('playbook-not-found');
      if (!res.ok) throw new Error('playbook-fetch-failed-' + res.status);
      return res.json();
    });
  }

  // ─── Masthead hydration ─────────────────────────────────────────────

  function hydrateMasthead(pb) {
    var titleEl = $('[data-pb-title]'); if (titleEl) titleEl.textContent = pb.title || '';
    var crumbEl = $('[data-pb-title-crumb]'); if (crumbEl) crumbEl.textContent = pb.title || '';

    var issueLabel = 'Issue No. ' + String(pb.issue_number || '—').padStart(2, '0');
    var issueEl = $('[data-pb-issue-number]'); if (issueEl) issueEl.textContent = issueLabel;
    var crumbIssueEl = $('[data-pb-issue-crumb]'); if (crumbIssueEl) crumbIssueEl.textContent = issueLabel;

    var period = pb.is_launch_volume ? 'Launch · Edition' : ('Vol. ' + (pb.volume || 'I'));
    var periodEl = $('[data-pb-period]'); if (periodEl) periodEl.textContent = period;
    var volEl = $('[data-pb-volume]'); if (volEl) volEl.textContent = 'Vol. ' + (pb.volume || 'I');

    var tagline = pb.subtitle || '';
    var taglineEl = $('[data-pb-tagline]');
    if (taglineEl) {
      if (tagline) { taglineEl.textContent = tagline; taglineEl.hidden = false; }
      else { taglineEl.hidden = true; }
    }

    var essayEl = $('[data-pb-essay]');
    if (essayEl) {
      clearChildren(essayEl);
      var essay = String(pb.persona_description || '');
      essay.split(/\n\n+/).forEach(function (para) {
        if (!para.trim()) return;
        var p = document.createElement('p');
        p.className = 'pb-prose';
        p.textContent = para.trim();
        essayEl.appendChild(p);
      });
      essayEl.setAttribute('data-pb-hydrated', 'true');
    }

    var coverIssueEl = $('[data-pb-cover-issue]'); if (coverIssueEl) coverIssueEl.textContent = issueLabel.replace('Issue No. ', 'Issue ');
    var coverPersonaEl = $('[data-pb-cover-persona]'); if (coverPersonaEl) coverPersonaEl.textContent = pb.title || '';
    var coverMonthEl = $('[data-pb-cover-month]'); if (coverMonthEl) coverMonthEl.textContent = pb.is_launch_volume ? 'LAUNCH · EDITION' : 'EDITION';
  }

  // ─── Module intro + toggle-pill labels ──────────────────────────────

  function hydrateModuleShells(pb) {
    MODULE_KEYS.forEach(function (mod) {
      var panel = $('[data-pb-panel="' + mod + '"]');
      if (!panel) return;
      var eyebrow = $('[data-pb-module-eyebrow]', panel);
      if (eyebrow) eyebrow.innerHTML = escapeHtml(MODULE_EYEBROWS[mod]).replace('·', '&middot;');
      var introEl = $('[data-pb-module-intro]', panel);
      var introCopy = (pb.modules && pb.modules[mod] && pb.modules[mod].intro_copy) || '';
      if (introEl) {
        introEl.textContent = introCopy;
        introEl.classList.remove('pb-skeleton');
        introEl.setAttribute('data-pb-hydrated', 'true');
      }
      var labelEl = $('[data-pb-toggle-label]', panel);
      if (labelEl) labelEl.textContent = TOGGLE_LABEL_BY_MODULE[mod];

      var levels = LEVELS_BY_MODULE[mod];
      var display = LEVEL_DISPLAY_BY_MODULE[mod];
      $$('[data-pb-toggle-pos]', panel).forEach(function (pill) {
        var pos = Number(pill.getAttribute('data-pb-toggle-pos'));
        var key = levels[pos - 1];
        var labelSpan = $('[data-pb-toggle-pill-label]', pill);
        if (labelSpan) labelSpan.textContent = display[key] || key;
      });
    });
  }

  // ─── Active-pill indicator (synced across all 4 module rows) ────────

  function refreshAllPillStates() {
    $$('[data-pb-toggle-pos]', modulesRoot).forEach(function (pill) {
      var pos = Number(pill.getAttribute('data-pb-toggle-pos'));
      pill.classList.toggle('is-active', pos === currentPosition);
      pill.setAttribute('aria-pressed', pos === currentPosition ? 'true' : 'false');
    });
  }

  // ─── Day-card rendering ─────────────────────────────────────────────

  function macrosAllZero(meal) {
    if (!meal) return true;
    return Number(meal.kcal || 0) === 0
      && Number(meal.protein_g || 0) === 0
      && Number(meal.carbs_g || 0) === 0
      && Number(meal.fat_g || 0) === 0;
  }

  function renderFuelDayContent(d, dayLabel) {
    var slots = ['morning', 'midday', 'snacks', 'evening'];
    var slotLabels = { morning: 'Morning', midday: 'Midday', snacks: 'Snacks', evening: 'Evening' };

    var cellsHtml = slots.map(function (slot) {
      var meal = d[slot] || {};
      var ingredients = Array.isArray(meal.ingredients) ? meal.ingredients : [];
      var portionStr = ingredients.join(' · ');
      var portionBlock = portionStr
        ? '<div class="pb-fuel-portion">' + escapeHtml(portionStr) + '</div>'
        : '';

      var macroRow = '';
      if (!macrosAllZero(meal)) {
        // Number-first markup per design — <strong>N</strong> precedes the unit.
        macroRow = ''
          + '<div class="pb-fuel-macros">'
          + '<span><strong>' + escapeHtml(meal.kcal || 0) + '</strong> kcal</span>'
          + '<span><strong>' + escapeHtml(meal.protein_g || 0) + 'g</strong> P</span>'
          + '<span><strong>' + escapeHtml(meal.carbs_g || 0) + 'g</strong> C</span>'
          + '<span><strong>' + escapeHtml(meal.fat_g || 0) + 'g</strong> F</span>'
          + '</div>';
      }
      return ''
        + '<div class="pb-fuel-cell">'
        + '<span class="pb-fuel-label">' + FUEL_SLOT_ICONS[slot] + escapeHtml(slotLabels[slot]) + '</span>'
        + '<p class="pb-fuel-desc">' + escapeHtml(meal.description || '') + '</p>'
        + portionBlock
        + macroRow
        + '</div>';
    }).join('');

    // Grid wraps the 4 meal cells. Totals are a SIBLING of the grid
    // (outside) — both belong to the .pb-day-row, both are tagged
    // data-pb-day-content so the expand/collapse logic toggles both.
    var gridHtml = '<div class="pb-day-grid pb-fuel-grid" data-pb-day-content hidden>' + cellsHtml + '</div>';

    var dayTotal = d.day_total || {};
    var totalsHtml = '';
    if (!macrosAllZero(dayTotal)) {
      totalsHtml = ''
        + '<div class="pb-fuel-totals" data-pb-day-content hidden>'
        +   '<span class="pb-fuel-totals-label">Day total &middot; ' + escapeHtml(dayLabel || '') + '</span>'
        +   '<div class="pb-fuel-totals-row">'
        +     '<span><strong>' + escapeHtml(dayTotal.kcal || 0) + '</strong><span class="pb-fuel-totals-unit">kcal</span></span>'
        +     '<span><strong>' + escapeHtml(dayTotal.protein_g || 0) + 'g</strong><span class="pb-fuel-totals-unit">protein</span></span>'
        +     '<span><strong>' + escapeHtml(dayTotal.carbs_g || 0) + 'g</strong><span class="pb-fuel-totals-unit">carbs</span></span>'
        +     '<span><strong>' + escapeHtml(dayTotal.fat_g || 0) + 'g</strong><span class="pb-fuel-totals-unit">fat</span></span>'
        +   '</div>'
        + '</div>';
    }

    return gridHtml + totalsHtml;
  }

  function renderMoveDayContent(d) {
    return ''
      + '<div class="pb-day-grid pb-prac-grid" data-pb-day-content hidden>'
      +   '<div class="pb-prac-title">' + escapeHtml(d.title || '') + '</div>'
      +   '<p class="pb-prac-body">' + escapeHtml(d.description || '') + '</p>'
      + '</div>';
  }

  // D12 placeholder rule for Heal: hide a sub-card whose title is a
  // TODO placeholder. The bulk-paste JSON convention uses an em-dash
  // (U+2014, "TODO — "); hyphen-minus is NOT matched on purpose — if
  // the editorial convention ever broadens, add the second clause here.
  var HEAL_TODO_PREFIX = 'TODO — ';

  function isHealTodoTitle(title) {
    if (typeof title !== 'string' || title.length === 0) return false;
    return title.indexOf(HEAL_TODO_PREFIX) === 0;
  }

  function renderHealDayContent(d) {
    // 3-column grid: stretch / sleep / recover. Hide a sub-card whose
    // title is a TODO placeholder (see isHealTodoTitle above).
    var subKeys = ['stretch', 'sleep', 'recover'];
    var subLabels = { stretch: 'Stretch', sleep: 'Sleep', recover: 'Recover' };
    var cells = subKeys.map(function (k) {
      var sub = d[k];
      if (!sub) return '';
      var title = sub.title;
      if (isHealTodoTitle(title)) return '';
      return ''
        + '<div class="pb-heal-cell">'
        +   '<span class="pb-fuel-label">' + HEAL_SUB_ICONS[k] + escapeHtml(subLabels[k]) + '</span>'
        +   '<div class="pb-prac-title">' + escapeHtml(title || '') + '</div>'
        +   '<p class="pb-prac-body">' + escapeHtml(sub.description || '') + '</p>'
        + '</div>';
    }).join('');
    return '<div class="pb-day-grid pb-heal-grid" data-pb-day-content hidden>' + cells + '</div>';
  }

  function renderSyncDayContent(d) {
    return ''
      + '<div class="pb-day-grid pb-prac-grid" data-pb-day-content hidden>'
      +   '<div class="pb-prac-title">' + escapeHtml(d.title || '') + '</div>'
      +   '<p class="pb-prac-body">' + escapeHtml(d.description || '') + '</p>'
      + '</div>';
  }

  function renderDayContentForModule(mod, d, dayLabel) {
    if (mod === 'fuel') return renderFuelDayContent(d, dayLabel);
    if (mod === 'move') return renderMoveDayContent(d);
    if (mod === 'heal') return renderHealDayContent(d);
    if (mod === 'sync') return renderSyncDayContent(d);
    return '';
  }

  function renderModuleDays(mod) {
    var panel = $('[data-pb-panel="' + mod + '"]');
    if (!panel || !payload) return;
    var levels = LEVELS_BY_MODULE[mod];
    var levelKey = levels[currentPosition - 1];
    var moduleData = (payload.modules && payload.modules[mod]) || {};
    var toggles = moduleData.toggles || {};
    var levelData = toggles[levelKey] || {};
    var rawDays = levelData.days || [];

    // Sort Mon→Sun for deterministic order (API already sorts; double-
    // ensure here in case of upstream drift).
    var daysByDow = {};
    rawDays.forEach(function (d) { daysByDow[d.day_of_week] = d; });
    var sortedDays = DAY_ORDER.map(function (dow) { return daysByDow[dow]; }).filter(Boolean);

    var grid = $('[data-pb-days]', panel);
    if (!grid) return;
    grid.innerHTML = sortedDays.map(function (d, i) {
      // day_tag rendered VERBATIM, including any "[COPY] " prefix (D12).
      var dayTag = String(d.day_tag || '');
      var dow = String(d.day_of_week || '');
      var dayName = DAY_LABELS[dow] || dow;
      var dayNum = String(i + 1).padStart(2, '0');  // "01", "02", …
      // Design markup: pb-day-row > pb-day-tag (button) > num/name/label/chev
      //   then sibling pb-day-grid (+ pb-fuel-totals for fuel) tagged
      //   [data-pb-day-content] so the expand/collapse logic toggles them.
      return ''
        + '<article class="pb-day-row" data-pb-day data-pb-day-dow="' + escapeHtml(dow) + '" data-module="' + escapeHtml(mod) + '">'
        +   '<button class="pb-day-tag pb-day-tag-toggle" type="button" data-pb-day-toggle aria-expanded="false">'
        +     '<div class="pb-day-num">' + dayNum + '</div>'
        +     '<div class="pb-day-name">' + escapeHtml(dayName) + '</div>'
        +     '<div class="pb-day-label">' + escapeHtml(dayTag) + '</div>'
        +     '<span class="pb-day-chev" aria-hidden="true">+</span>'
        +   '</button>'
        +   renderDayContentForModule(mod, d, dayName)
        + '</article>';
    }).join('');

    bindDayInteractions(panel);
    // Re-apply the shared openDays Set so a re-rendered Mon that was
    // open before stays open after the portion-toggle.
    applyOpenStateToPanel(panel);
    updateExpandCount(panel);
  }

  function renderAllModuleDays() {
    MODULE_KEYS.forEach(function (mod) { renderModuleDays(mod); });
  }

  // ─── Per-day expand/collapse + bulk actions ─────────────────────────

  // A pb-day-row may contain MULTIPLE [data-pb-day-content] elements
  // (Fuel rows have both .pb-fuel-grid and .pb-fuel-totals; non-Fuel
  // rows have a single grid). setDayOpen toggles ALL of them in lockstep,
  // keeps the row's is-open + aria-expanded in sync, AND mirrors the
  // change into the SHARED openDays Set so the open/closed state
  // survives every re-render path (portion toggle, tab switch) AND
  // stays consistent across all four modules.
  //
  // The `recordState` flag is `true` for user-driven changes (per-row
  // click + bulk expand/collapse) and `false` for re-render restoration
  // (renderModuleDays + bindTabs) so restoring state doesn't redundantly
  // add to a Set it's reading from.
  function setDayOpen(dayEl, isOpen, recordState) {
    dayEl.classList.toggle('is-open', isOpen);
    var toggle = $('[data-pb-day-toggle]', dayEl);
    if (toggle) toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    $$('[data-pb-day-content]', dayEl).forEach(function (c) { c.hidden = !isOpen; });
    if (recordState) {
      var dow = dayEl.getAttribute('data-pb-day-dow');
      if (dow) {
        if (isOpen) openDays.add(dow);
        else openDays.delete(dow);
      }
    }
  }

  // Apply the shared openDays Set to every [data-pb-day] in `panel`.
  // Used by renderModuleDays after a re-render AND by bindTabs when a
  // panel becomes visible.
  function applyOpenStateToPanel(panel) {
    $$('[data-pb-day]', panel).forEach(function (dayEl) {
      var dow = dayEl.getAttribute('data-pb-day-dow');
      setDayOpen(dayEl, openDays.has(dow), false);
    });
  }

  function bindDayInteractions(panel) {
    $$('[data-pb-day]', panel).forEach(function (day) {
      var toggle = $('[data-pb-day-toggle]', day);
      if (!toggle) return;
      toggle.addEventListener('click', function () {
        var willOpen = !day.classList.contains('is-open');
        // User-driven: record the change AND propagate to every other
        // module's visible day rows so the bulk counter + active panel
        // stay in sync with the shared Set.
        setDayOpen(day, willOpen, true);
        syncAllPanels();
      });
    });
  }

  function updateExpandCount(panel) {
    var days = $$('[data-pb-day]', panel);
    var open = days.filter(function (d) { return d.classList.contains('is-open'); });
    var countEl = $('[data-pb-count]', panel);
    if (countEl) countEl.textContent = open.length + ' of ' + days.length + ' days expanded';
    var expandBtn = $('[data-pb-expand]', panel);
    var collapseBtn = $('[data-pb-collapse]', panel);
    if (expandBtn) expandBtn.disabled = open.length === days.length;
    if (collapseBtn) collapseBtn.disabled = open.length === 0;
  }

  // Re-applies openDays + refreshes the bulk counter on EVERY module's
  // panel. Called after a user-driven open/close on any module so the
  // other 3 modules (which aren't visible right now) carry the same
  // state when the user switches to them.
  function syncAllPanels() {
    MODULE_KEYS.forEach(function (mod) {
      var panel = $('[data-pb-panel="' + mod + '"]');
      if (!panel) return;
      applyOpenStateToPanel(panel);
      updateExpandCount(panel);
    });
  }

  function bindBulkActions() {
    MODULE_KEYS.forEach(function (mod) {
      var panel = $('[data-pb-panel="' + mod + '"]');
      if (!panel) return;
      var expand = $('[data-pb-expand]', panel);
      var collapse = $('[data-pb-collapse]', panel);
      if (expand) expand.addEventListener('click', function () {
        // Bulk expand: fill the shared Set with all 7 dows, then mirror
        // across every module.
        DAY_ORDER.forEach(function (dow) { openDays.add(dow); });
        syncAllPanels();
      });
      if (collapse) collapse.addEventListener('click', function () {
        openDays.clear();
        syncAllPanels();
      });
    });
  }

  // ─── Tab navigation (preserves position on switch — D10c) ───────────

  function bindTabs() {
    $$('[data-pb-tab]', modulesRoot).forEach(function (tab) {
      tab.addEventListener('click', function () {
        var which = tab.getAttribute('data-pb-tab');
        $$('[data-pb-tab]', modulesRoot).forEach(function (t) {
          t.classList.toggle('active', t === tab);
        });
        var newPanel = null;
        $$('[data-pb-panel]', modulesRoot).forEach(function (p) {
          var match = p.getAttribute('data-pb-panel') === which;
          p.hidden = !match;
          if (match) newPanel = p;
        });
        // Days were rendered for ALL modules on payload load + on every
        // toggle change — no re-render needed on tab switch. Re-apply
        // the SHARED openDays Set to the newly-visible panel so it
        // mirrors whatever the user expanded on any other module
        // (syncAllPanels does this proactively, but tab-switch redoes it
        // defensively in case the previous panel was hidden + missed
        // a sync — cheap, just a class+attribute flip per row).
        if (newPanel) {
          applyOpenStateToPanel(newPanel);
          updateExpandCount(newPanel);
        }
      });
    });
  }

  // ─── Synced toggle (D10a/D10b — clicking any pill moves all 4) ──────

  function bindToggle() {
    $$('[data-pb-toggle-pos]', modulesRoot).forEach(function (pill) {
      pill.addEventListener('click', function () {
        var pos = Number(pill.getAttribute('data-pb-toggle-pos'));
        if (!pos || pos < 1 || pos > 4) return;
        if (pos === currentPosition) return;
        currentPosition = pos;
        refreshAllPillStates();
        renderAllModuleDays();
        renderGrocery();
        renderWeekTotals();  // XL totals ≠ medium; recompute on every toggle.
      });
    });
  }

  // ─── Week-preview strip ("The shape of seven days") ─────────────────
  //
  // Static — hydrated ONCE from Fuel.medium.days[i].day_tag (the canonical
  // running-context labels: Easy / Intervals / Recover / Tempo / Easy /
  // Long output / Rest day). Does NOT depend on currentPosition because
  // day_tag is the same across positions for a given day.

  function hydrateWeekStrip() {
    if (!payload) return;
    var fuelMedium = payload.modules && payload.modules.fuel && payload.modules.fuel.toggles && payload.modules.fuel.toggles.medium;
    var days = (fuelMedium && fuelMedium.days) || [];
    var byDow = {};
    days.forEach(function (d) { byDow[d.day_of_week] = d; });
    DAY_ORDER.forEach(function (dow, i) {
      var cell = $('[data-pb-week-cell="' + dow + '"]');
      if (!cell) return;
      var d = byDow[dow];
      var dayEl   = $('[data-pb-week-day]',   cell);
      var labelEl = $('[data-pb-week-label]', cell);
      var indexEl = $('[data-pb-week-index]', cell);
      if (dayEl)   dayEl.textContent   = DAY_LABELS[dow] || dow;
      if (labelEl) labelEl.textContent = (d && d.day_tag) || '';
      if (indexEl) indexEl.textContent = String(i + 1).padStart(2, '0');
    });
  }

  // ─── Weekly totals card ("The week, by the numbers") ────────────────
  //
  // Computes from the 7 day_total objects of the CURRENT fuel position.
  // Recomputes on every portion-toggle (XL ≈ 28k kcal vs medium 16,078)
  // — that's the load-bearing piece of this section.

  function sumDayTotals(days) {
    return days.reduce(function (acc, d) {
      var t = (d && d.day_total) || {};
      return {
        kcal:    acc.kcal    + Number(t.kcal || 0),
        protein: acc.protein + Number(t.protein_g || 0),
        carbs:   acc.carbs   + Number(t.carbs_g || 0),
        fat:     acc.fat     + Number(t.fat_g || 0),
      };
    }, { kcal: 0, protein: 0, carbs: 0, fat: 0 });
  }

  function renderWeekTotals() {
    var container = $('[data-pb-week-totals]');
    if (!container || !payload) return;
    var levels = LEVELS_BY_MODULE.fuel;
    var levelKey = levels[currentPosition - 1];
    var fuelLevel = payload.modules && payload.modules.fuel && payload.modules.fuel.toggles && payload.modules.fuel.toggles[levelKey];
    var days = (fuelLevel && fuelLevel.days) || [];
    if (days.length === 0) { container.hidden = true; return; }

    var totals = sumDayTotals(days);
    var dayCount = days.length;
    var avg = {
      kcal:    Math.round(totals.kcal    / dayCount),
      protein: Math.round(totals.protein / dayCount),
      carbs:   Math.round(totals.carbs   / dayCount),
      fat:     Math.round(totals.fat     / dayCount),
    };
    var portionDisplay = (LEVEL_DISPLAY_BY_MODULE.fuel[levelKey] || levelKey || 'medium');

    container.innerHTML = ''
      + '<div class="pb-week-totals-head">'
      +   '<span class="caption" style="color:var(--pb-accent,var(--rise));">Weekly macros</span>'
      +   '<h3 class="pb-week-totals-title">The week, <em>by the numbers</em>.</h3>'
      +   '<p class="pb-week-totals-sub">Totals reflect the ' + escapeHtml(portionDisplay) + ' portion across ' + dayCount + ' days. Use them to track week-to-week or scale up if you’re training harder than this template assumes.</p>'
      + '</div>'
      + '<div class="pb-week-totals-grid">'
      +   '<div class="pb-week-totals-col">'
      +     '<div class="pb-week-totals-col-label">Total · ' + dayCount + ' days</div>'
      +     '<div class="pb-week-totals-stats">'
      +       '<div class="pb-week-totals-stat"><span class="pb-week-totals-stat-num">' + totals.kcal.toLocaleString() + '</span><span class="pb-week-totals-stat-unit">kcal</span></div>'
      +       '<div class="pb-week-totals-stat"><span class="pb-week-totals-stat-num">' + totals.protein + '<small>g</small></span><span class="pb-week-totals-stat-unit">Protein</span></div>'
      +       '<div class="pb-week-totals-stat"><span class="pb-week-totals-stat-num">' + totals.carbs + '<small>g</small></span><span class="pb-week-totals-stat-unit">Carbs</span></div>'
      +       '<div class="pb-week-totals-stat"><span class="pb-week-totals-stat-num">' + totals.fat + '<small>g</small></span><span class="pb-week-totals-stat-unit">Fat</span></div>'
      +     '</div>'
      +   '</div>'
      +   '<div class="pb-week-totals-col">'
      +     '<div class="pb-week-totals-col-label">Daily average</div>'
      +     '<div class="pb-week-totals-stats">'
      +       '<div class="pb-week-totals-stat"><span class="pb-week-totals-stat-num">' + avg.kcal.toLocaleString() + '</span><span class="pb-week-totals-stat-unit">kcal / day</span></div>'
      +       '<div class="pb-week-totals-stat"><span class="pb-week-totals-stat-num">' + avg.protein + '<small>g</small></span><span class="pb-week-totals-stat-unit">Protein</span></div>'
      +       '<div class="pb-week-totals-stat"><span class="pb-week-totals-stat-num">' + avg.carbs + '<small>g</small></span><span class="pb-week-totals-stat-unit">Carbs</span></div>'
      +       '<div class="pb-week-totals-stat"><span class="pb-week-totals-stat-num">' + avg.fat + '<small>g</small></span><span class="pb-week-totals-stat-unit">Fat</span></div>'
      +     '</div>'
      +   '</div>'
      + '</div>';
    container.hidden = false;
  }

  // ─── Grocery list rendering (per-position) ──────────────────────────
  //
  // Uses the NEW shape directly: each category is { label, note?, items }
  // and each item is { name, qty } — no string splitting. Re-renders on
  // every portion toggle (the synced toggle calls renderGrocery alongside
  // renderAllModuleDays + renderWeekTotals, so scaled qtys swap in lockstep).

  function renderGrocery() {
    if (!payload) return;
    var levels = LEVELS_BY_MODULE.fuel;  // grocery uses fuel keys (D8)
    var levelKey = levels[currentPosition - 1];
    var g = (payload.grocery_lists && payload.grocery_lists[levelKey]) || {};
    var cats = Array.isArray(g.categories) ? g.categories : [];

    // Sub-copy counts + portion sentence.
    var itemCount = cats.reduce(function (n, c) { return n + (Array.isArray(c.items) ? c.items.length : 0); }, 0);
    var itemCountEl = $('[data-pb-grocery-item-count]');
    var catCountEl  = $('[data-pb-grocery-cat-count]');
    var portionEl   = $('[data-pb-grocery-portion-sentence]');
    if (itemCountEl) itemCountEl.textContent = String(itemCount);
    if (catCountEl)  catCountEl.textContent  = String(cats.length);
    if (portionEl) {
      // Authored intro_copy from data — already mentions the portion word.
      // Falls back to a synthesized sentence if absent.
      portionEl.textContent = String(g.intro_copy || '').replace(
        /^One trip on Sunday covers all seven days .*?categories\.\s*/,
        ''
      ) || 'Quantities scaled for this portion; adjust up if you’re cooking for two.';
    }

    var grid = $('[data-pb-grocery-grid]');
    if (grid) {
      if (cats.length === 0) {
        grid.innerHTML = '<p class="pb-grocery-sub" style="grid-column: 1 / -1; opacity:0.7;">No grocery list for this position yet.</p>';
      } else {
        grid.innerHTML = cats.map(function (cat) {
          var items = Array.isArray(cat.items) ? cat.items : [];
          var noteHtml = cat.note
            ? '<span class="pb-grocery-cat-note">' + escapeHtml(cat.note) + '</span>'
            : '';
          var count = String(items.length).padStart(2, '0');
          var itemsHtml = items.map(function (it) {
            return ''
              + '<li class="pb-grocery-item">'
              +   '<span class="pb-grocery-check" aria-hidden="true"></span>'
              +   '<span class="pb-grocery-name">' + escapeHtml(it.name || '') + '</span>'
              +   '<span class="pb-grocery-qty">'  + escapeHtml(it.qty  || '') + '</span>'
              + '</li>';
          }).join('');
          return ''
            + '<div class="pb-grocery-cat">'
            +   '<header class="pb-grocery-cat-head">'
            +     '<h4 class="pb-grocery-cat-label">' + escapeHtml(cat.label || '') + '</h4>'
            +     noteHtml
            +     '<span class="pb-grocery-cat-count">' + count + '</span>'
            +   '</header>'
            +   '<ul class="pb-grocery-items">' + itemsHtml + '</ul>'
            + '</div>';
        }).join('');
      }
    }

    renderCartCta(g);
  }

  // ─── Print + Copy actions (design page-playbooks.jsx:951-963) ───────

  function buildCopyText() {
    if (!payload) return '';
    var levels = LEVELS_BY_MODULE.fuel;
    var levelKey = levels[currentPosition - 1];
    var g = (payload.grocery_lists && payload.grocery_lists[levelKey]) || {};
    var cats = Array.isArray(g.categories) ? g.categories : [];
    return cats.map(function (cat) {
      var items = Array.isArray(cat.items) ? cat.items : [];
      return String(cat.label || '').toUpperCase()
        + '\n'
        + items.map(function (it) { return '- ' + (it.name || '') + ' · ' + (it.qty || ''); }).join('\n');
    }).join('\n\n');
  }

  function bindGroceryActions() {
    var printBtn = $('[data-pb-grocery-print]');
    if (printBtn) {
      printBtn.addEventListener('click', function () { window.print(); });
    }
    var copyBtn = $('[data-pb-grocery-copy]');
    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        var text = buildCopyText();
        if (!text || !navigator.clipboard) return;
        navigator.clipboard.writeText(text).then(function () {
          // Ephemeral confirmation — swap the label for ~2s, then revert.
          var original = copyBtn.innerHTML;
          copyBtn.innerHTML = 'Copied <span class="arr">&#10003;</span>';
          copyBtn.disabled = true;
          setTimeout(function () {
            copyBtn.innerHTML = original;
            copyBtn.disabled = false;
          }, 2000);
        }).catch(function () { /* silent — design doesn't surface failure either */ });
      });
    }
  }

  function renderCartCta(g) {
    var cta = $('[data-pb-cart-cta]');
    if (!cta) return;
    var products = Array.isArray(g.circadia_products) ? g.circadia_products : [];
    var resolved = products
      .map(function (p) { return { handle: p.handle, qty: Number(p.qty || 0), variant_id: variantMap[p.handle] }; })
      .filter(function (p) { return p.variant_id && p.qty > 0; });

    if (resolved.length === 0) {
      cta.hidden = true;
      return;
    }
    cta.hidden = false;
    var totalCans = resolved.reduce(function (acc, p) { return acc + p.qty; }, 0);
    var note = $('[data-pb-cart-cta-note]', cta);
    if (note) note.textContent = totalCans + (totalCans === 1 ? ' can' : ' cans') + ' · this week’s rhythm';

    var addBtn = $('[data-pb-cart-add]', cta);
    if (addBtn) {
      addBtn.onclick = function () { addToCart(resolved); };
    }
  }

  // ─── Cart-add (multi-item via /cart/add.js + items[]) ───────────────
  //
  // After-add flow mirrors the established theme convention (chrome.js
  // PDP/collection submit handler at lines 1452-1462): open the drawer
  // imperatively (body.cart-open + [data-cart-drawer].open +
  // [data-cart-overlay].open) and call window.Circadia.refreshCart().
  // refreshCart internally dispatches 'circadia:cart-changed' which is
  // what case-aggregation.js + the inventory-shortfall handler listen
  // to. NO custom event from here — drift would mean other listeners
  // don't pick up the change.

  function openDrawer() {
    document.body.classList.add('cart-open');
    var d = $('[data-cart-drawer]');
    var o = $('[data-cart-overlay]');
    if (d) d.classList.add('open');
    if (o) o.classList.add('open');
  }

  function addToCart(resolved) {
    var form = new FormData();
    resolved.forEach(function (p, i) {
      form.append('items[' + i + '][id]', String(p.variant_id));
      form.append('items[' + i + '][quantity]', String(p.qty));
    });
    fetch('/cart/add.js', { method: 'POST', body: form, credentials: 'same-origin' })
      .then(function (r) {
        if (r.status >= 500) {
          return r.text().then(function (t) {
            throw new Error('cart/add.js HTTP ' + r.status + ': ' + t);
          });
        }
        return r.json().catch(function () { return {}; });
      })
      .then(function () {
        openDrawer();
        if (window.Circadia && typeof window.Circadia.refreshCart === 'function') {
          window.Circadia.refreshCart();
        }
      })
      .catch(function (err) {
        // Silent failure mirrors the established PDP/collection cart-add
        // path (which logs + leaves the user to retry). The drawer-open
        // flow only fires on success.
        if (window.console && console.error) console.error('playbook cart add failed:', err);
      });
  }

  // ─── Error UI (404 / fetch fail) ────────────────────────────────────

  function renderError(err) {
    var titleEl = $('[data-pb-title]');
    var essayEl = $('[data-pb-essay]');
    var msg = err && err.message === 'playbook-not-found'
      ? 'This Playbook is in development.'
      : 'We couldn’t load this Playbook right now.';
    if (titleEl) titleEl.textContent = 'Coming Soon';
    if (essayEl) {
      clearChildren(essayEl);
      var p = document.createElement('p');
      p.className = 'pb-prose';
      p.textContent = msg + ' Sign up to the Dispatch and we’ll let you know when it’s live.';
      essayEl.appendChild(p);
      essayEl.setAttribute('data-pb-hydrated', 'true');
    }
    // Hide everything below the masthead.
    if (modulesRoot) modulesRoot.hidden = true;
  }

  // ─── Boot ───────────────────────────────────────────────────────────

  fetchPlaybook().then(function (data) {
    payload = data.playbook || data;  // tolerate either envelope shape
    hydrateMasthead(payload);
    hydrateModuleShells(payload);
    hydrateWeekStrip();      // static; once at boot
    bindTabs();
    bindToggle();
    bindBulkActions();
    bindGroceryActions();    // Print + Copy buttons (Phase 2C)
    refreshAllPillStates();
    renderAllModuleDays();
    renderGrocery();
    renderWeekTotals();      // dynamic; recomputes on every toggle change
  }).catch(renderError);
})();
